import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { SERVER_URL } from './config';
import { AppContext } from './App';
import LocationSearch from './LocationSearch';
import './Info.css';

const MAX_FILE_SIZE_MB = 2;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;
const MAX_SHUTTLE_LOCATIONS = 3;

function Info() {
  const { user } = useContext(AppContext);

  const [info, setInfo] = useState({});
  const [editedInfo, setEditedInfo] = useState({});
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(false);

  // Hebrew
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [fileError, setFileError] = useState('');

  // English
  const [selectedFileEn, setSelectedFileEn] = useState(null);
  const [previewUrlEn, setPreviewUrlEn] = useState(null);
  const [fileErrorEn, setFileErrorEn] = useState('');

  // French
  const [selectedFileFr, setSelectedFileFr] = useState(null);
  const [previewUrlFr, setPreviewUrlFr] = useState(null);
  const [fileErrorFr, setFileErrorFr] = useState('');

  // flags: use Hebrew invitation for EN/FR
  const [useHeEn, setUseHeEn] = useState(false);
  const [useHeFr, setUseHeFr] = useState(false);

  // modal EN/FR
  const [showLangModal, setShowLangModal] = useState(false);
  const [langTab, setLangTab] = useState('en'); // 'en' | 'fr'

  // shuttle locations: NEW SHAPE [{id?, name_i18n:{he,en,fr}}]
  const [shuttleLocations, setShuttleLocations] = useState([]);

  const invitationOptions = [
    {
      serverPath: 'https://mariage1234.s3.eu-central-1.amazonaws.com/image1.jpeg',
      localPath: '/image1.png',
    },
    {
      serverPath: 'https://mariage1234.s3.eu-central-1.amazonaws.com/image2.jpeg',
      localPath: '/image2.png',
    },
    {
      serverPath: 'https://mariage1234.s3.eu-central-1.amazonaws.com/image3.jpeg',
      localPath: '/image3.png',
    },
  ];

  useEffect(() => {
    const getInfo = async () => {
      try {
        const response = await axios.get(`${SERVER_URL}info/`, {
          headers: { Authorization: `Token ${localStorage.getItem('token')}` },
        });

        const data = response.data || {};
        const invitation = data.invitation || '';

        const rawEn = data.invitation_en || data.invitationEn || '';
        const rawFr = data.invitation_fr || data.invitationFr || '';

        // אם אין עברית עדיין – אל "ננעל" על להשתמש בעברית (כדי שלא ייתקע)
        const initialUseHeEn = !!invitation && (!rawEn || rawEn === invitation);
        const initialUseHeFr = !!invitation && (!rawFr || rawFr === invitation);

        setUseHeEn(initialUseHeEn);
        setUseHeFr(initialUseHeFr);

        const normalized = {
          ...data,

          invitation_en: rawEn || invitation,
          invitation_fr: rawFr || invitation,

          bride_name_en: data.bride_name_en ?? data.brideNameEn ?? '',
          groom_name_en: data.groom_name_en ?? data.groomNameEn ?? '',
          hall_en: data.hall_en ?? data.hallEn ?? '',
          hebrew_date_en: data.hebrew_date_en ?? data.hebrewDateEn ?? '',

          bride_name_fr: data.bride_name_fr ?? data.brideNameFr ?? '',
          groom_name_fr: data.groom_name_fr ?? data.groomNameFr ?? '',
          hall_fr: data.hall_fr ?? data.hallFr ?? '',
          hebrew_date_fr: data.hebrew_date_fr ?? data.hebrewDateFr ?? '',

          shuttle_locations: data.shuttle_locations || data.shuttleLocations || [],
        };

        setInfo(normalized);
        setEditedInfo(normalized);

        // ===== shuttle_locations normalization =====
        // Expected backend: [{id, name_i18n:{he,en,fr}}]
        // Backward-compat: {name, language}
        const normalizedShuttles = (normalized.shuttle_locations || []).map((x) => {
          const name_i18n =
            x.name_i18n && typeof x.name_i18n === 'object' && !Array.isArray(x.name_i18n) ? x.name_i18n : null;

          if (!name_i18n) {
            const lang = String(x.language || 'he').toLowerCase();
            const name = String(x.name || '');
            return {
              id: x.id,
              name_i18n: {
                he: lang === 'he' ? name : '',
                en: lang === 'en' ? name : '',
                fr: lang === 'fr' ? name : '',
              },
            };
          }

          return {
            id: x.id,
            name_i18n: {
              he: String(name_i18n.he || ''),
              en: String(name_i18n.en || ''),
              fr: String(name_i18n.fr || ''),
            },
          };
        });

        setShuttleLocations(normalizedShuttles);

        const withOriginal = (normalized.dates || []).map((d) => ({
          id: d.id,
          type: d.type,
          date: d.date,
          originalDate: d.date,
        }));
        setDates(withOriginal);
      } catch (error) {
        console.error('Error fetching user info:', error);
        alert('Failed to fetch user info. Please log in again.');
      }
    };

    getInfo();
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'message' && value.length > 300) return;

    // אם מורידים ask_drive -> ננקה את כל מקורות ההסעה בכל השפות
    if (name === 'ask_drive' && type === 'checkbox' && !checked) {
      setEditedInfo((prev) => ({
        ...prev,
        ask_drive: false,
      }));
      setShuttleLocations([]);
      return;
    }

    setEditedInfo((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleLocationSelect = (location) => {
    setEditedInfo((prev) => ({
      ...prev,
      latitude: parseFloat(location.lat.toFixed(6)),
      longitude: parseFloat(location.lng.toFixed(6)),
      address: location.name,
      ask_kids: prev.ask_kids ?? true,
      ask_vegetarian: prev.ask_vegetarian ?? true,
      ask_drive: prev.ask_drive ?? true,
      kids_age: prev.kids_age ?? 10,
      rides: prev.rides ?? '',
    }));
  };

  const normalizeUrl = (url) => {
    if (!url) return '';
    const trimmed = url.trim();
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
  };

  const isValidUrl = (url) => {
    if (!url) return true;
    try {
      new URL(normalizeUrl(url));
      return true;
    } catch {
      return false;
    }
  };

  const validateFileOrAlert = (file) => {
    if (!file) return false;
    if (file.size > MAX_FILE_SIZE) {
      alert(`התמונה גדולה מדי – מעל ${MAX_FILE_SIZE_MB}MB. בחרי קובץ קטן יותר.`);
      return false;
    }
    return true;
  };

  const uploadToS3 = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const uploadResponse = await axios.post(`${SERVER_URL}upload/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return uploadResponse.data.url || '';
  };

  // Hebrew upload selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFileError('');
    if (!validateFileOrAlert(file)) {
      e.target.value = null;
      setPreviewUrl(null);
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleLangFileChange = (lang, e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (lang === 'en') {
      setFileErrorEn('');
      setUseHeEn(false);
    }
    if (lang === 'fr') {
      setFileErrorFr('');
      setUseHeFr(false);
    }

    if (!validateFileOrAlert(file)) {
      e.target.value = null;
      if (lang === 'en') {
        setPreviewUrlEn(null);
        setSelectedFileEn(null);
      }
      if (lang === 'fr') {
        setPreviewUrlFr(null);
        setSelectedFileFr(null);
      }
      return;
    }

    if (lang === 'en') {
      setSelectedFileEn(file);
      setPreviewUrlEn(URL.createObjectURL(file));
    } else if (lang === 'fr') {
      setSelectedFileFr(file);
      setPreviewUrlFr(URL.createObjectURL(file));
    }
  };

  const sendPreviewMessage = async (lang) => {
    const label = lang === 'en' ? 'אנגלית' : lang === 'fr' ? 'צרפתית' : 'עברית';
    const ok = window.confirm(`לשלוח אלייך הודעת תצוגה ב${label}?`);
    if (!ok) return;

    try {
      const response = await axios.get(`${SERVER_URL}send-preview-message/`, {
        headers: { Authorization: `Token ${localStorage.getItem('token')}` },
        params: lang ? { lang } : undefined,
      });

      alert(response.data?.message || 'נשלח');
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;

      console.error('sendPreviewMessage failed', { status, data, error });

      const msg =
        data?.message ||
        (status ? `שליחת הודעת התצוגה נכשלה (שגיאה ${status}).` : 'שליחת הודעת התצוגה נכשלה.');

      const extra = data?.details
        ? `\n\nפרטים:\n${
            typeof data.details === 'string' ? data.details : JSON.stringify(data.details, null, 2)
          }`
        : '';

      alert(msg + extra);
    }
  };

  // ===== shuttle helpers =====
  const setShuttleName = (idx, lang, value) => {
    setShuttleLocations((prev) =>
      prev.map((p, i) => {
        if (i !== idx) return p;
        const curr = p.name_i18n && typeof p.name_i18n === 'object' ? p.name_i18n : {};
        return { ...p, name_i18n: { ...curr, [lang]: value } };
      })
    );
  };

  const addShuttleLocation = () => {
    setShuttleLocations((prev) => {
      if (prev.length >= MAX_SHUTTLE_LOCATIONS) return prev;
      return [...prev, { name_i18n: { he: '', en: '', fr: '' } }];
    });
  };

  const removeShuttleLocation = (idx) => {
    setShuttleLocations((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSaveAll = async () => {
    const askDrive = !!editedInfo.ask_drive;

    const cleanedShuttle = shuttleLocations
      .map((x) => {
        const i18n = x.name_i18n && typeof x.name_i18n === 'object' ? x.name_i18n : {};
        const he = String(i18n.he || '').trim();
        const en = String(i18n.en || '').trim();
        const fr = String(i18n.fr || '').trim();

        const name_i18n = {};
        if (he) name_i18n.he = he;
        if (en) name_i18n.en = en;
        if (fr) name_i18n.fr = fr;

        return {
          ...(x.id ? { id: x.id } : {}),
          name_i18n,
        };
      })
      .filter((x) => x.name_i18n && Object.keys(x.name_i18n).length > 0);

    if (askDrive && cleanedShuttle.length === 0) {
      alert('אם סימנת לשאול על הסעה – חובה להוסיף לפחות מקום אחד להסעה (בעברית/אנגלית/צרפתית).');
      return;
    }

    if (cleanedShuttle.length > MAX_SHUTTLE_LOCATIONS) {
      alert('אפשר עד 3 מקומות להסעה.');
      return;
    }

    setLoading(true);

    setFileError('');
    setFileErrorEn('');
    setFileErrorFr('');

    try {
      // 1) Hebrew
      let uploadedHe = editedInfo.invitation;

      if (selectedFile) {
        try {
          uploadedHe = await uploadToS3(selectedFile);
          setSelectedFile(null);
          setPreviewUrl(null);
        } catch (error) {
          if (error.response?.status === 413) {
            setFileError(`התמונה גדולה מדי – מעל ${MAX_FILE_SIZE_MB}MB.`);
          } else {
            alert('שגיאה בהעלאת התמונה.');
          }
          return;
        }
      }

      // 2) English
      let uploadedEn = uploadedHe;
      if (!useHeEn) {
        uploadedEn = editedInfo.invitation_en || editedInfo.invitationEn || uploadedHe;

        if (selectedFileEn) {
          try {
            uploadedEn = await uploadToS3(selectedFileEn);
            setSelectedFileEn(null);
            setPreviewUrlEn(null);
          } catch (error) {
            if (error.response?.status === 413) {
              setFileErrorEn(`התמונה גדולה מדי – מעל ${MAX_FILE_SIZE_MB}MB.`);
            } else {
              alert('שגיאה בהעלאת התמונה באנגלית.');
            }
            return;
          }
        }
      }

      // 3) French
      let uploadedFr = uploadedHe;
      if (!useHeFr) {
        uploadedFr = editedInfo.invitation_fr || editedInfo.invitationFr || uploadedHe;

        if (selectedFileFr) {
          try {
            uploadedFr = await uploadToS3(selectedFileFr);
            setSelectedFileFr(null);
            setPreviewUrlFr(null);
          } catch (error) {
            if (error.response?.status === 413) {
              setFileErrorFr(`התמונה גדולה מדי – מעל ${MAX_FILE_SIZE_MB}MB.`);
            } else {
              alert('שגיאה בהעלאת התמונה בצרפתית.');
            }
            return;
          }
        }
      }

      const massagesPayload = dates.map((d) => ({
        id: d.id,
        date: d.date,
      }));

      const updatedData = {
        ...editedInfo,
        invitation: uploadedHe,
        invitation_en: uploadedEn,
        invitation_fr: uploadedFr,
        massages: massagesPayload,
        rides: normalizeUrl(editedInfo.rides),
        shuttle_locations: cleanedShuttle, // NEW SHAPE for backend
      };

      await axios.post(`${SERVER_URL}info/`, updatedData, {
        headers: { Authorization: `Token ${localStorage.getItem('token')}` },
      });

      setInfo(updatedData);
      setEditedInfo(updatedData);

      if (useHeEn) {
        setEditedInfo((prev) => ({ ...prev, invitation_en: updatedData.invitation }));
      }
      if (useHeFr) {
        setEditedInfo((prev) => ({ ...prev, invitation_fr: updatedData.invitation }));
      }

      alert('כל השינויים נשמרו בהצלחה');
    } catch (error) {
      alert('שגיאה בשמירת השינויים');
    } finally {
      setLoading(false);
    }
  };

  const heInvitationPreview =
    previewUrl ||
    (!invitationOptions.some((o) => o.serverPath === editedInfo.invitation)
      ? editedInfo.invitation
      : editedInfo.invitation || null);

  const enInvitationPreview = useHeEn
    ? previewUrl || editedInfo.invitation || ''
    : previewUrlEn || editedInfo.invitation_en || editedInfo.invitationEn || '';

  const frInvitationPreview = useHeFr
    ? previewUrl || editedInfo.invitation || ''
    : previewUrlFr || editedInfo.invitation_fr || editedInfo.invitationFr || '';

  const setLangUseHe = (lang, useHe) => {
    if (lang === 'en') {
      setUseHeEn(useHe);
      setSelectedFileEn(null);
      setPreviewUrlEn(null);
      setFileErrorEn('');

      setEditedInfo((prev) => {
        const he = prev.invitation || '';
        const curr = prev.invitation_en || prev.invitationEn || '';
        if (useHe) return { ...prev, invitation_en: he };
        const keepIfDifferent = curr && curr !== he ? curr : '';
        return { ...prev, invitation_en: keepIfDifferent };
      });
    }

    if (lang === 'fr') {
      setUseHeFr(useHe);
      setSelectedFileFr(null);
      setPreviewUrlFr(null);
      setFileErrorFr('');

      setEditedInfo((prev) => {
        const he = prev.invitation || '';
        const curr = prev.invitation_fr || prev.invitationFr || '';
        if (useHe) return { ...prev, invitation_fr: he };
        const keepIfDifferent = curr && curr !== he ? curr : '';
        return { ...prev, invitation_fr: keepIfDifferent };
      });
    }
  };

  // ===== Extra language details UI (EN/FR) =====
  const renderExtraLangDetails = (lang) => {
    const isEn = lang === 'en';

    const brideKey = isEn ? 'bride_name_en' : 'bride_name_fr';
    const groomKey = isEn ? 'groom_name_en' : 'groom_name_fr';
    const hallKey = isEn ? 'hall_en' : 'hall_fr';
    const hebrewDateKey = isEn ? 'hebrew_date_en' : 'hebrew_date_fr';

    return (
      <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
        <div style={{ fontWeight: 800, marginBottom: 6 }}>
          {isEn ? 'פרטי אירוע באנגלית (אופציונלי)' : 'פרטי אירוע בצרפתית (אופציונלי)'}
        </div>
        <div style={{ opacity: 0.85, marginBottom: 10 }}>
          אם תשאירי ריק — נשתמש בפרטים בעברית כברירת מחדל.
        </div>

        <label>
          שם הכלה ({isEn ? 'EN' : 'FR'}):
          <input
            type="text"
            name={brideKey}
            value={editedInfo[brideKey] || ''}
            onChange={handleInputChange}
            placeholder={isEn ? 'Bride name' : 'Nom de la mariée'}
          />
        </label>

        <label>
          שם החתן ({isEn ? 'EN' : 'FR'}):
          <input
            type="text"
            name={groomKey}
            value={editedInfo[groomKey] || ''}
            onChange={handleInputChange}
            placeholder={isEn ? 'Groom name' : 'Nom du marié'}
          />
        </label>

        <label>
          שם האולם ({isEn ? 'EN' : 'FR'}):
          <input
            type="text"
            name={hallKey}
            value={editedInfo[hallKey] || ''}
            onChange={handleInputChange}
            placeholder={isEn ? 'Hall name' : 'Nom de la salle'}
          />
        </label>

        <label>
          תאריך עברי ({isEn ? 'EN' : 'FR'}) — טקסט חופשי:
          <input
            type="text"
            name={hebrewDateKey}
            value={editedInfo[hebrewDateKey] || ''}
            onChange={handleInputChange}
            placeholder={isEn ? 'e.g. 23 Kislev / Leil...' : 'ex. 23 Kislev / Leil...'}
          />
        </label>
      </div>
    );
  };

  // ===== Modal (EN/FR) =====
  const renderLangModal = () => {
    if (!showLangModal) return null;

    const overlayStyle = {
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.45)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: 16,
    };

    const boxStyle = {
      background: '#fff',
      borderRadius: 16,
      padding: 18,
      width: 'min(860px, 100%)',
      maxHeight: '90vh',
      overflowY: 'auto',
      position: 'relative',
      textAlign: 'right',
    };

    const isEn = langTab === 'en';

    return (
      <div style={overlayStyle} role="dialog" aria-modal="true">
        <div style={boxStyle}>
          <button
            type="button"
            onClick={() => setShowLangModal(false)}
            aria-label="סגירה"
            style={{
              position: 'absolute',
              top: 10,
              left: 10,
              border: 'none',
              background: 'transparent',
              fontSize: 22,
              cursor: 'pointer',
            }}
          >
            ×
          </button>

          <h3 style={{ marginTop: 0, marginBottom: 6 }}>אנגלית / צרפתית (אופציונלי)</h3>
          <div style={{ opacity: 0.85, marginBottom: 12 }}>
            כאן אפשר להעלות הזמנה לשפה נוספת וגם למלא פרטי אירוע לאותה שפה. השינויים נשמרים בלחיצה על{' '}
            <b>“שמור שינויים”</b> בתחתית העמוד.
          </div>

          <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
            <button
              type="button"
              onClick={() => setLangTab('en')}
              style={{
                padding: '8px 12px',
                borderRadius: 12,
                border: '1px solid rgba(0,0,0,0.15)',
                background: isEn ? 'rgba(0,0,0,0.06)' : 'white',
                cursor: 'pointer',
                fontWeight: isEn ? 800 : 600,
              }}
            >
              English
            </button>

            <button
              type="button"
              onClick={() => setLangTab('fr')}
              style={{
                padding: '8px 12px',
                borderRadius: 12,
                border: '1px solid rgba(0,0,0,0.15)',
                background: !isEn ? 'rgba(0,0,0,0.06)' : 'white',
                cursor: 'pointer',
                fontWeight: !isEn ? 800 : 600,
              }}
            >
              Français
            </button>
          </div>

          {/* Invitation uploader + preview + preview-message */}
          {langTab === 'en' && (
            <div>
              <div style={{ fontWeight: 800, marginBottom: 8 }}>הזמנה באנגלית</div>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <input type="checkbox" checked={useHeEn} onChange={(e) => setLangUseHe('en', e.target.checked)} />
                להשתמש באותה תמונה כמו בעברית
              </label>

              <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div>
                  <label className="file-upload-label">
                    <input type="file" accept="image/*" onChange={(e) => handleLangFileChange('en', e)} disabled={useHeEn} />
                    {selectedFileEn ? 'נבחרה תמונה (לחצי להחליף)' : 'העלאת תמונה באנגלית'}
                  </label>

                  {!!fileErrorEn && <div className="file-error">{fileErrorEn}</div>}

                  <div style={{ marginTop: 10 }}>
                    <button type="button" className="preview-btn" onClick={() => sendPreviewMessage('en')}>
                      שלח אליי הודעת תצוגה באנגלית
                    </button>
                  </div>
                </div>

                <div>
                  {enInvitationPreview ? (
                    <img
                      src={enInvitationPreview}
                      alt="English invitation preview"
                      style={{ width: 260, borderRadius: 12, border: '1px solid rgba(0,0,0,0.1)' }}
                    />
                  ) : null}
                </div>
              </div>

              {renderExtraLangDetails('en')}
            </div>
          )}

          {langTab === 'fr' && (
            <div>
              <div style={{ fontWeight: 800, marginBottom: 8 }}>הזמנה בצרפתית</div>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <input type="checkbox" checked={useHeFr} onChange={(e) => setLangUseHe('fr', e.target.checked)} />
                להשתמש באותה תמונה כמו בעברית
              </label>

              <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div>
                  <label className="file-upload-label">
                    <input type="file" accept="image/*" onChange={(e) => handleLangFileChange('fr', e)} disabled={useHeFr} />
                    {selectedFileFr ? 'נבחרה תמונה (לחצי להחליף)' : 'העלאת תמונה בצרפתית'}
                  </label>

                  {!!fileErrorFr && <div className="file-error">{fileErrorFr}</div>}

                  <div style={{ marginTop: 10 }}>
                    <button type="button" className="preview-btn" onClick={() => sendPreviewMessage('fr')}>
                      שלח אליי הודעת תצוגה בצרפתית
                    </button>
                  </div>
                </div>

                <div>
                  {frInvitationPreview ? (
                    <img
                      src={frInvitationPreview}
                      alt="French invitation preview"
                      style={{ width: 260, borderRadius: 12, border: '1px solid rgba(0,0,0,0.1)' }}
                    />
                  ) : null}
                </div>
              </div>

              {renderExtraLangDetails('fr')}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <button type="button" onClick={() => setShowLangModal(false)}>
              סגור
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="background" aria-hidden="true" />

      <div className="info-container">
        <h2>מידע אישי</h2>

        <div className="edit-form">
          <label>
            שם פרטי:
            <input type="text" name="first_name" value={editedInfo.first_name || ''} onChange={handleInputChange} />
          </label>

          <label>
            שם משפחה:
            <input type="text" name="last_name" value={editedInfo.last_name || ''} onChange={handleInputChange} />
          </label>

          <label>
            שם הכלה:
            <input type="text" name="bride_name" value={editedInfo.bride_name || ''} onChange={handleInputChange} />
          </label>

          <label>
            שם החתן:
            <input type="text" name="groom_name" value={editedInfo.groom_name || ''} onChange={handleInputChange} />
          </label>

          <label>
            טלפון:
            <input type="text" name="phone" value={editedInfo.phone || ''} onChange={handleInputChange} />
          </label>

          <label>
            אימייל:
            <input type="email" name="email" value={editedInfo.email || ''} onChange={handleInputChange} />
          </label>

          <label>
            שם האולם:
            <input type="text" name="hall" value={editedInfo.hall || ''} onChange={handleInputChange} />
          </label>

          <label>
            <span>עדכן כתובת אולם (שם האולם מעדכנים בנפרד):</span>
            <LocationSearch onLocationSelect={handleLocationSelect} defaultValue={editedInfo.address} />
          </label>

          <label>
            תאריך:
            <input type="date" name="date" value={editedInfo.date || ''} onChange={handleInputChange} />
          </label>

          <label>
            תאריך עברי (אפשר לציין יום, למשל: יום שלישי, ג' אייר):
            <input type="text" name="hebrew_date" value={editedInfo.hebrew_date || ''} onChange={handleInputChange} />
          </label>

          <label>
            שעה:
            <input type="time" name="time" value={editedInfo.time || ''} onChange={handleInputChange} />
          </label>

          <div className="terms-section">
            <label>
              <input type="checkbox" name="ask_kids" checked={!!editedInfo.ask_kids} onChange={handleInputChange} />
              <span>לשאול את המוזמנים האם מגיעים ילדים</span>
            </label>

            <label>
              <input type="checkbox" name="ask_vegetarian" checked={!!editedInfo.ask_vegetarian} onChange={handleInputChange} />
              <span>לשאול את המוזמנים האם הם צריכים מנות צמחוניות</span>
            </label>

            <label>
              <input type="checkbox" name="ask_drive" checked={!!editedInfo.ask_drive} onChange={handleInputChange} />
              <span>לשאול את המוזמנים האם הם מגיעים בהסעה</span>
            </label>
          </div>

          {/* Shuttle locations */}
          {editedInfo.ask_drive && (
            <div className="shuttle-box">
              <div className="shuttle-title">מאיפה ההסעה? (עד 3 מקומות) — שם בעברית / English / Français</div>

              {shuttleLocations.map((loc, idx) => (
                <div key={loc.id ?? `new-${idx}`} className="shuttle-row" style={{ flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ fontWeight: 800 }}>מקום #{idx + 1}</div>

                    <button
                      type="button"
                      className="shuttle-remove"
                      onClick={() => removeShuttleLocation(idx)}
                      aria-label="מחיקה"
                      style={{ marginInlineStart: 'auto' }}
                    >
                      מחק
                    </button>
                  </div>

                  <label style={{ display: 'grid', gap: 6 }}>
                    <span style={{ fontWeight: 700 }}>עברית</span>
                    <input
                      type="text"
                      value={loc.name_i18n?.he || ''}
                      onChange={(e) => setShuttleName(idx, 'he', e.target.value)}
                      placeholder="למשל: ירושלים"
                    />
                  </label>

                  <label style={{ display: 'grid', gap: 6 }}>
                    <span style={{ fontWeight: 700 }}>English</span>
                    <input
                      type="text"
                      value={loc.name_i18n?.en || ''}
                      onChange={(e) => setShuttleName(idx, 'en', e.target.value)}
                      placeholder="e.g. Jerusalem"
                    />
                  </label>

                  <label style={{ display: 'grid', gap: 6 }}>
                    <span style={{ fontWeight: 700 }}>Français</span>
                    <input
                      type="text"
                      value={loc.name_i18n?.fr || ''}
                      onChange={(e) => setShuttleName(idx, 'fr', e.target.value)}
                      placeholder="ex. Jérusalem"
                    />
                  </label>
                </div>
              ))}

              <button
                type="button"
                className="shuttle-add"
                disabled={shuttleLocations.length >= MAX_SHUTTLE_LOCATIONS}
                onClick={addShuttleLocation}
              >
                + הוספת מקום
              </button>
            </div>
          )}

          {dates.length > 0 && (
            <div className="massages-section">
              <p className="massages-text">תאריכי שליחת הודעות ושיחות מתוכננים:</p>

              {dates.map((date, index) => (
                <div key={index} className="massage-item">
                  {date.type === 'event_day' ? (
                    <>
                      <label className="massage-type">📅 הודעת תזכורת ביום האירוע</label>
                      <input type="date" value={date.date} disabled />
                    </>
                  ) : (
                    <>
                      <label className="massage-type">{date.type === 'call' ? '📞 שיחה' : '💬 הודעה'}</label>
                      <input
                        type="date"
                        value={date.date}
                        onChange={(e) => {
                          const updated = [...dates];
                          updated[index] = {
                            ...updated[index],
                            date: e.target.value,
                            originalDate: updated[index].originalDate || updated[index].date,
                          };
                          setDates(updated);
                        }}
                      />
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          <label>
            אם ברצונכם להוסיף הערה להזמנה שתישלח רשמו כאן (עד 300 תווים):
            <textarea name="message" value={editedInfo.message || ''} onChange={handleInputChange} maxLength={300} />
          </label>

          <div>
            <label>בחר הזמנה:</label>

            <div className="invitation-options">
              {invitationOptions.map((option, idx) => (
                <div
                  key={idx}
                  className={`invitation-option ${editedInfo.invitation === option.serverPath ? 'selected' : ''}`}
                  onClick={() => setEditedInfo((prev) => ({ ...prev, invitation: option.serverPath }))}
                >
                  <img src={option.localPath} alt={`Invitation ${idx + 1}`} />
                </div>
              ))}

              <div
                className={`upload-option ${
                  !invitationOptions.some((o) => o.serverPath === editedInfo.invitation) && editedInfo.invitation
                    ? 'selected'
                    : ''
                }`}
              >
                <label className="file-upload-label">
                  <input type="file" name="customInvitation" accept="image/*" onChange={handleFileChange} />
                  {selectedFile ? 'נבחרה תמונה (לחצי להעלות חדשה)' : 'העלאת תמונה משלך'}
                </label>

                {previewUrl && <img src={previewUrl} alt="Preview" className="invitation-preview-he" />}
                {!!fileError && <div className="file-error">{fileError}</div>}
              </div>
            </div>

            {/* button opens modal */}
            <div className="foreign-block">
              <div className="foreign-block-title">אורחים מחו״ל (אופציונלי)</div>
              <div className="foreign-block-subtitle">עריכה של הזמנה + פרטי אירוע באנגלית/צרפתית (לא חובה).</div>

              <button
                type="button"
                className="foreign-open-btn"
                onClick={() => {
                  setLangTab('en');
                  setShowLangModal(true);
                }}
              >
                עריכה באנגלית / צרפתית
              </button>

              {renderLangModal()}
            </div>
          </div>

          <div className="group-links">
            <label>
              קישור לקבוצת הסעות/טרמפים
              <input
                type="text"
                name="rides"
                placeholder="למשל: chat.whatsapp.com/abcdef..."
                value={editedInfo.rides || ''}
                onChange={handleInputChange}
                onBlur={(e) => {
                  const v = e.target.value;
                  if (!v) return;
                  if (!isValidUrl(v)) {
                    alert('הקישור לטרמפים לא תקין.');
                    return;
                  }
                  setEditedInfo((prev) => ({ ...prev, rides: normalizeUrl(v) }));
                }}
              />
            </label>
          </div>

          <label>
            רוצה לראות את ההודעה שתישלח לאורחים שלך?
            <button type="button" onClick={() => sendPreviewMessage(null)}>
              לחצי כאן וההודעה תישלח אליך לוואטסאפ
            </button>
          </label>

          <button className="save-all-button" onClick={handleSaveAll} disabled={loading || !!fileError || !!fileErrorEn || !!fileErrorFr}>
            {loading ? 'שומר שינויים...' : 'שמור שינויים'}
          </button>

          {loading && <div className="loading-spinner">⏳ טוען... אנא המתיני</div>}
        </div>
      </div>
    </>
  );
}

export default Info;
